import React, { useState, useEffect } from 'react';
import {
  FaSave,
  FaHome,
  FaCloudUploadAlt,
  FaSpinner,
  FaCheckCircle,
  FaExclamationCircle,
  FaImage,
  FaTimes,
  FaCalendarAlt,
  FaQuoteRight,
} from 'react-icons/fa';
import { useAdmin } from '../context/AdminContext';
import { MEDIA_BASE_URL } from '../../config';
import './SectionManager.css';
import './HomeManager.css';

const HERO_DESC_LIMIT = 220;

// 🎯 PURE JS CANVAS IMAGE COMPRESSOR
const compressImage = (file, maxWidth = 1920, maxHeight = 1080, quality = 0.8) => {
  return new Promise((resolve, reject) => {
    if (file.size <= 500 * 1024) return resolve(file);
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        let { width, height } = img;
        if (width > height) {
          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
          }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => {
            if (!blob) return reject(new Error('Canvas is empty'));
            resolve(
              new File(
                [blob],
                file.name.replace(/\.[^/.]+$/, '') + '.jpg',
                { type: 'image/jpeg', lastModified: Date.now() }
              )
            );
          },
          'image/jpeg',
          quality
        );
      };
      img.onerror = (err) => reject(err);
    };
    reader.onerror = (err) => reject(err);
  });
};

const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const HomeManager = () => {
  const { data, updateSection, loading } = useAdmin();

  const [form, setForm] = useState({
    heroTitle: '',
    heroDesc: '',
    tagline: '',
    yearEstablished: '',
  });

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [isCompressing, setIsCompressing] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [sizeInfo, setSizeInfo] = useState({ original: '', compressed: '' });

  useEffect(() => {
    if (data?.home) {
      setForm({
        heroTitle: data.home.heroTitle || '',
        heroDesc: data.home.heroDesc || '',
        tagline: data.home.tagline || '',
        yearEstablished: data.home.yearEstablished
          ? String(data.home.yearEstablished)
          : '',
      });
      if (data.home.heroImage) {
        const imgUrl = data.home.heroImage.startsWith('http')
          ? data.home.heroImage
          : `${MEDIA_BASE_URL}${data.home.heroImage}`;
        setImagePreview(imgUrl);
      }
    }
  }, [data?.home]);

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file (JPG, PNG, WEBP).');
      return;
    }

    try {
      setIsCompressing(true);
      const originalSizeFormatted = formatFileSize(file.size);
      const compressed = await compressImage(file);
      const compressedSizeFormatted = formatFileSize(compressed.size);

      setImageFile(compressed);
      setImagePreview(URL.createObjectURL(compressed));
      setSizeInfo({
        original: originalSizeFormatted,
        compressed: compressedSizeFormatted,
      });
      setError('');
    } catch (err) {
      console.error('Compression Error:', err);
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    } finally {
      setIsCompressing(false);
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview('');
    setSizeInfo({ original: '', compressed: '' });
    const input = document.getElementById('home-image-input');
    if (input) input.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if ((form.heroDesc || '').length > HERO_DESC_LIMIT) {
      setError(`Hero Description must be ${HERO_DESC_LIMIT} characters or less.`);
      return;
    }

    setSaving(true);
    try {
      const payload = { ...form };
      if (imageFile) payload.imageFile = imageFile;

      await updateSection('home', payload);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('Home save error:', err);
      setError('Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const descLen = (form.heroDesc || '').length;

  if (loading && !data?.home?.heroTitle) {
    return (
      <div className="sg-manager">
        <div className="hm-loader">
          <FaSpinner className="spin" />
          <p>Loading home content...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="sg-manager">
      {/* ═══════════════════════════════════════════
          PAGE HEADER
         ═══════════════════════════════════════════ */}
      <div className="sg-page-header">
        <div className="sg-ph-left">
          <div className="sg-ph-icon">
            <FaHome />
          </div>
          <div className="sg-ph-text">
            <h2>Home Page Content</h2>
            <p>Manage your website's hero section and landing page details</p>
          </div>
        </div>
      </div>

      {/* Alerts */}
      {saved && (
        <div className="sg-alert sg-alert-success">
          <FaCheckCircle /> Home page content saved successfully!
        </div>
      )}

      {error && (
        <div className="sg-alert sg-alert-error">
          <FaExclamationCircle /> {error}
        </div>
      )}

      {/* ═══════════════════════════════════════════
          MAIN CONTENT - SPLIT LAYOUT
         ═══════════════════════════════════════════ */}
      <div className="hm-split-layout">
        {/* LEFT: FORM */}
        <div className="sg-form-card hm-form-block">
          <div className="sg-form-header">
            <div>
              <h3>Homepage Configuration</h3>
              <p>Update hero section and corporate identity</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="sg-form">
            {/* Hero Title */}
            <div className="sg-field">
              <label>
                <FaQuoteRight className="hm-field-icon" /> Hero Title{' '}
                <span className="req">*</span>
              </label>
              <input
                type="text"
                value={form.heroTitle}
                onChange={(e) => setForm({ ...form, heroTitle: e.target.value })}
                placeholder="e.g. Where Trust Meets Transparency"
                required
              />
            </div>

            {/* Hero Description */}
            <div className="sg-field">
              <label className="sg-label-flex">
                <span>Hero Description</span>
                <span
                  className={`sg-char-count ${
                    descLen >= HERO_DESC_LIMIT ? 'over' : ''
                  }`}
                >
                  {descLen}/{HERO_DESC_LIMIT}
                </span>
              </label>
              <textarea
                value={form.heroDesc}
                onChange={(e) =>
                  setForm({
                    ...form,
                    heroDesc: e.target.value.substring(0, HERO_DESC_LIMIT),
                  })
                }
                rows="5"
                maxLength={HERO_DESC_LIMIT}
                placeholder="Brief description that appears on the homepage hero section..."
              />
            </div>

            {/* Tagline + Year */}
            <div className="hm-form-row">
              <div className="sg-field">
                <label>Corporate Tagline</label>
                <input
                  type="text"
                  value={form.tagline}
                  onChange={(e) =>
                    setForm({ ...form, tagline: e.target.value })
                  }
                  placeholder="e.g. Building Legacy Together"
                />
              </div>
              <div className="sg-field">
                <label>
                  <FaCalendarAlt className="hm-field-icon" /> Year Established
                </label>
                <input
                  type="text"
                  value={form.yearEstablished}
                  onChange={(e) =>
                    setForm({ ...form, yearEstablished: e.target.value })
                  }
                  placeholder="e.g. 2021"
                />
              </div>
            </div>

            {/* Save Button */}
            <div className="sg-form-actions">
              <button
                type="submit"
                className="sg-btn-gold hm-save-btn"
                disabled={saving || isCompressing}
              >
                {saving ? (
                  <>
                    <FaSpinner className="spin" /> Saving...
                  </>
                ) : (
                  <>
                    <FaSave /> Save Changes
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* RIGHT: IMAGE UPLOAD */}
        <div className="hm-image-panel">
          <div className="sg-form-card hm-image-card">
            <div className="sg-form-header">
              <div>
                <h3>Hero Background</h3>
                <p>Upload the main banner image for your homepage</p>
              </div>
            </div>

            {/* Image Preview Area */}
            <div className="hm-image-preview">
              {imagePreview ? (
                <div className="hm-preview-wrap">
                  <img src={imagePreview} alt="Hero Preview" />
                  <div className="hm-preview-overlay">
                    <div className="hm-preview-info">
                      <FaCheckCircle />
                      <span>Image loaded successfully</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="hm-empty-preview">
                  <FaImage />
                  <p>No image uploaded yet</p>
                  <span>Recommended: 1920×1080px</span>
                </div>
              )}
            </div>

            {/* Upload Actions */}
            <div className="hm-upload-actions">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                style={{ display: 'none' }}
                id="home-image-input"
                disabled={isCompressing || saving}
              />

              <label
                htmlFor="home-image-input"
                className={`hm-upload-btn ${isCompressing ? 'disabled' : ''}`}
              >
                {isCompressing ? (
                  <>
                    <FaSpinner className="spin" /> Compressing...
                  </>
                ) : (
                  <>
                    <FaCloudUploadAlt />
                    {imagePreview ? 'Change Image' : 'Choose Image'}
                  </>
                )}
              </label>

              {imagePreview && (
                <button
                  type="button"
                  className="hm-remove-btn"
                  onClick={removeImage}
                  disabled={isCompressing}
                >
                  <FaTimes /> Remove
                </button>
              )}
            </div>

            {/* Compression Info */}
            {sizeInfo.compressed && (
              <div className="hm-size-info">
                <FaCheckCircle />
                <div>
                  <strong>Image Optimized</strong>
                  <span>
                    {sizeInfo.original} → {sizeInfo.compressed}
                  </span>
                </div>
              </div>
            )}

            {/* Info Note */}
            <div className="hm-info-note">
              <strong>Tip:</strong> High-quality corporate photos work best.
              Images are auto-compressed for faster loading.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomeManager;